import Vue from 'vue'
import Vuex from 'vuex'

import Party from '@/api/party'
import PartyRelationship from '@/api/party-relationship'
import ProductOrder from '@/api/product-order'
import Item from '@/api/item'
import { getInstance } from '@/auth/auth0-plugin'

// Modules
import sources from './modules/source'

Vue.use(Vuex)

const namespaced = true
const modules = { sources }
const strict = false

const state = {
  userRelatedParties: [],
  productOrderTypes: [],
  productOrderStatuses: [],
  stores: [],
  departmentCategoryMapping: [],
  categorySubcategoryMapping: [],
  sourceRels: []
}

const mutations = {
  setDepartmentCategoryMapping(state, payload) {
    state.departmentCategoryMapping = payload
  },
  setCategorySubcategoryMapping(state, payload) {
    state.categorySubcategoryMapping = payload
  },
  setUserRelatedParties (state, payload) {
    state.userRelatedParties = payload
  },
  setProductOrderTypes (state, payload) {
    state.productOrderTypes = payload
  },
  setProductOrderStatuses (state, payload) {
    state.productOrderStatuses = payload
  },
  setStores (state, payload) {
    state.stores = payload
  },
  setSourceRels (state, payload) {
    state.sourceRels = payload
  }
}

const actions = {
  async getSourceRels(context) {
    if (context.getters.sourceRels.length > 0) return
    let values = []
    const res = await Item.getSourceRels()
    if (res?.data?.length > 0) {
      values = res.data
    }
    return context.commit('setSourceRels', values)
  },
  async getDepartmentCategoryMapping (context, params) {
    let departmentCategoryMapping = []
    try {
      const { data } = await Item.getPossibleValues('department', 'category', params.sources, params.is_retailer)
      if (data) {
        departmentCategoryMapping = data
      }
      return context.commit('setDepartmentCategoryMapping', departmentCategoryMapping)
    } catch (err) {
      console.error(err)
    }
  },
  async getCategorySubcategoryMapping (context, params) {
    let categorySubcategoryMapping = []
    try {
      const { data } = await Item.getPossibleValues('category', 'subcategory', params.sources, params.is_retailer)
      if (data) {
        categorySubcategoryMapping = data
      }
      return context.commit('setCategorySubcategoryMapping', categorySubcategoryMapping)
    } catch (err) {
      console.error(err)
    }
  },
  async getUserRelatedParties (context) {
    const userParties = context.getters.userRelatedParties
    if (userParties.length > 0) return

    const authService = getInstance()
    let relatedParties = []
    const res = await Party.getUserProfile(authService.user.email)
    if (res?.data?.length > 0) {
      relatedParties = res.data
    }
    return context.commit('setUserRelatedParties', relatedParties)
  },
  async getProductOrderTypes(context) {
    const productOrderTypes = context.getters.productOrderTypes
    if (productOrderTypes.length > 0) return
    let values = []
    const res = await ProductOrder.getProductOrderTypes()
    if (res?.data?.length > 0) {
      values = res.data
    }
    return context.commit('setProductOrderTypes', values)
  },
  async getProductOrderStatuses(context) {
    const productOrderStatuses = context.getters.productOrderStatuses
    if (productOrderStatuses.length > 0) return
    let values = []
    const res = await ProductOrder.getProductOrderStatuses()
    if (res?.data?.length > 0) {
      values = res.data
    }
    return context.commit('setProductOrderStatuses', values)
  },
  async getStores(context) {
    if (context.getters.stores.length > 0) return
    let stores = []
    let unprocessed_data = []
    let offset = 0
    const limit = 250
    let keepFetching = true

    while (keepFetching) {
      const { data } = await Party.searchParties({party_types: ['STORE']}, offset, limit)

      if (!data?.length) break

      unprocessed_data.push(...data)
      if (data.length < limit) {
        keepFetching = false
      } else {
        offset += limit
      }
    }

    const filteredStores = unprocessed_data.flatMap(store => {
      if (store.attributes?.PRODUCT_CATALOG_STORE === 'True') {
        return formatStore(store)
      }
      return []
    })

    await context.dispatch('getSourceRels')

    stores = await setPartyItemSources(filteredStores, context.getters.sourceRels)
    return context.commit('setStores', stores)
  }
}

const getters = {
  departmentCategoryMapping (state) {
    return state.departmentCategoryMapping
  },
  categorySubcategoryMapping (state) {
    return state.categorySubcategoryMapping
  },
  userRelatedParties (state) {
    return state.userRelatedParties
  },
  productOrderTypes (state) {
    return state.productOrderTypes
  },
  productOrderStatuses (state) {
    return state.productOrderStatuses
  },
  stores (state) {
    return state.stores
  },
  sourceRels (state) {
    return state.sourceRels
  }
}

async function setPartyItemSources(stores, sourceRels) {
  if (stores.length === 0) return []
    let mapped = []
    const partyIds = stores.map(s => s.id)
    const res = await getUpstreamPartyRels(partyIds)
    if (res?.data?.length > 0) {
      const upstreamRels = res.data.filter(r => {
        return (partyIds.includes(r.from_party_id))
          && ['SUPPLIED_BY', 'DSD_SUPPLIED_BY', 'ALT_SUPPLIED_BY'].includes(r.from_rel_type)
      })

      if (sourceRels.length > 0) {
        mapped = stores.map(store => {
          const party_id = store.id
          const dcRel = upstreamRels.find(r => {
            return (r.from_party_id === party_id) && (r.from_rel_type === 'SUPPLIED_BY')
          })
          const dsdRel = upstreamRels.find(r => {
            return (r.from_party_id === party_id) && (r.from_rel_type === 'DSD_SUPPLIED_BY')
          })
          const dcSourceRel = sourceRels.find(result => dcRel?.to_party_id === result.party_id)
          const dsdSourceRel = sourceRels.find(result => dsdRel?.to_party_id === result.party_id)
          const altDcRels = upstreamRels.filter(r => {
            return (r.from_party_id === party_id) && (r.from_rel_type === 'ALT_SUPPLIED_BY')
          })
          let altDcSources = []
          if (altDcRels.length > 0) {
            altDcRels.forEach(rel => {
              const altDcSourceRel = sourceRels.find(result => rel?.to_party_id === result.party_id)
              if (altDcSourceRel) {
                altDcSources.push(altDcSourceRel.source_id)
              }
            })
          }
          const dsd_source = dsdSourceRel?.source_id || null
          const dc_source = dcSourceRel?.source_id || null
          if (dsd_source) {
            altDcSources.push(dsd_source)
          }
          return { ...store, dc_source, dsd_source, alt_dc_sources: altDcSources }
        })
      }
    }
    return mapped
}

async function getUpstreamPartyRels(partyIds) {
  if (partyIds.length === 0) return []
  const payload = {
    from_party_ids: partyIds,
    excluded_upstream_types: ['WHOLESALER', 'REGION_GROUP', 'AD_GROUP', 'OWNER', 'STORE_GROUP']
  }
  return PartyRelationship.getUpstreamRelationships(payload)
}

function formatStore(party) {
  let display_name = party.party_name || party.name
  if (party.attributes) {
    let attrVal = party.attributes['WHOLESALER_STORE_NUMBER'] || ''
    if (attrVal) {
      attrVal = `#${attrVal}`
    }
    display_name = `${attrVal} ${display_name}`
  }
  party.name = display_name

  // build store price_key string
  // needed to display correct unit & case cost values from product price_data object
  let price_key = null
  const markup = party.attributes?.STORE_PRICE_MARKUP
  const price_list = party.attributes?.STORE_PRICE_LIST

  if (markup && price_list) {
    const price_list_prefix = price_list.split('-')[0] // e.g. SEA from SEA-PMC
    price_key = markup.startsWith('JBG')
      ? markup.replaceAll('-', '_').toLowerCase() // e.g. jbg_hibase
      : `${price_list_prefix.toLowerCase()}_${markup.toLowerCase()}` // e.g. sea_mkt
  }
  party.price_key = price_key

  const { STORE_PRICE_LIST, STORE_PRICE_MARKUP, STORE_PRICE_ZONE, STORE_PRICE_MARGIN } = party.attributes
  const catalog_name = [
    STORE_PRICE_LIST,
    STORE_PRICE_MARKUP, 
    (STORE_PRICE_ZONE || ''),
    (STORE_PRICE_MARGIN || '')
  ].join('')
  party.catalog_name = catalog_name

  return party
}

export default new Vuex.Store({
  state,
  strict,
  actions,
  modules,
  getters,
  mutations,
  namespaced
})
